import React, { useContext, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import Icon from '@common/assets';
import JobGroupArea from '@components/jobs/filterLayer/jobGroupArea';
import CheckboxArea from '@components/jobs/filterLayer/checkboxArea';
import { LatestOpenType } from '@components/jobs';
import { CalendarContext } from '@components/jobs/contextWrap';
import { useCalendarCount, useCalendarFilter } from '@repository/calendar/useCalendar';
import { useUserInfo } from '@repository/auth/useUserInfo';
import { CalendarFilterFormVO } from '@domain/vo/calendar/CalendarFilterFormVO';
import { RecruitCategoryTypeNameMap } from '@domain/constant/recruitCategoryType';
import { LocationShortFilter } from '@domain/constant/locationType';
import { BusinessSizeEnumKo } from '@domain/constant/businessSize';
import ObjectUtil from '@utils/object/util';
import classnames from 'classnames/bind';
import styles from './filterLayer.module.scss';
const cx = classnames.bind(styles);

interface FilterLayerProps {
  isEnableKeyDown: boolean;
  setLatestOpen: (latestOpen: LatestOpenType) => void;
}

const FilterLayer = ({ isEnableKeyDown, setLatestOpen }: FilterLayerProps) => {
  const { filter } = useContext(CalendarContext);
  //jobGroupModal 뷰 상태값
  const [isVisibleJobGroup, setIsVisibleJobGroup] = useState(false);
  const { reset, watch, getValues } = useFormContext();
  const isFetch =
    filter.visibleFilter &&
    !ObjectUtil.isKeysValueAllEmpty(watch(), ['jobTitles', 'recruitmentTypes', 'businessSizeTypes', 'locations']);

  const { data: count } = useCalendarCount(watch() as CalendarFilterFormVO, isFetch);

  const jobPostCountText = isFetch && count !== undefined ? `적용(${count}건)` : '적용';

  //유저정보 조회
  const { data: userData } = useUserInfo();
  //유저정보 조회 enabled값
  const isLogin = !!userData?.id;

  //캘린더 필터 데이터 조회
  const { data: calendarFilter } = useCalendarFilter(isLogin, (vo) =>
    filter.setLatestFilter(new CalendarFilterFormVO(vo)),
  );

  const handleResetBtn = () => {
    const keyword = watch('keyword');
    //필터부분만 리셋해야함.
    reset({ ...new CalendarFilterFormVO(), keyword, targetMonth: watch('targetMonth') });
  };

  const onCloseFilter = () => {
    //jobGroupModal이 열려있으면 filter창이 닫히면 안 됨.
    if (!isVisibleJobGroup) handleCloseFilter();
  };

  const handleCloseFilter = () => {
    filter.setVisibleFilter(false);
    setLatestOpen(null);
    //keyword는 값에 영향을 받으면 안 됨.
    const keyword = getValues('keyword');
    reset({ ...filter.latestFilter, keyword });
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    switch (e.key) {
      case 'Escape':
        if (isEnableKeyDown) onCloseFilter();
        return;
    }
  };

  useEffect(() => {
    if (calendarFilter) reset(new CalendarFilterFormVO(calendarFilter));
  }, [calendarFilter]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
    //handleKeyDown상태값 업데이트 위해 isVisibleJobGroup설정
  }, [isVisibleJobGroup, isEnableKeyDown]);

  return (
    // 필터 영역
    <div className={cx('filterLayerArea', { visible: filter.visibleFilter })}>
      {/* 닫기 버튼 */}
      <a className={cx('btnClose')} role="button" onClick={handleCloseFilter}>
        <Icon name="closeLight" width={24} height={24} />
      </a>

      {/* 필터 내용 영역 */}
      <div className={cx('filterInner')}>
        {/* 직군 · 직무 영역 */}
        <JobGroupArea prefix={'jobTitles'} isVisible={isVisibleJobGroup} setIsVisible={setIsVisibleJobGroup} />

        {/* 채용 형태 */}
        <CheckboxArea prefix={'recruitmentTypes'} title={'채용 형태'} enumTypes={RecruitCategoryTypeNameMap} />

        {/* 기업 구분 */}
        <CheckboxArea prefix={'businessSizeTypes'} title={'기업 구분'} enumTypes={BusinessSizeEnumKo} />

        {/* 근무 지역 */}
        <CheckboxArea prefix={'locations'} title={'근무 지역'} enumTypes={LocationShortFilter} />
      </div>

      <div className={cx('btnArea')}>
        <a className={cx('btnReset')} role="button" onClick={handleResetBtn}>
          <Icon name="resetLight" width={24} height={24} />
          필터 초기화
        </a>

        {/* [D] 필터 선택 안된 경우 disabled 처리 */}
        <button className={cx('btnApply')} type="submit">
          {jobPostCountText}
        </button>
      </div>
    </div>
  );
};

export default FilterLayer;
